import { T } from 'src/shared/helpers';
import { ComposerOptionActionsType } from './composerAttributesModels';

export type ComposerUnsupportedActionType = 'SHOW_HOTSPOT' | 'HIDE_HOTSPOT';
export type ComposerActionIcon =
    'visibility' | 'visibilityOff' | 'object' | 'part' | 'colour' | 'camera' | 'design' | 'pivot' | 'reset' | 'setting' | 'print' | 'hotspot';

// LE FAMIGLIE DEI TIPI (prototipo, utente 2026-10-20): nel menu dei tipi si leggono separate da un filo, in quest'ordine.
export type ComposerActionFamily = 'visibility' | 'materials' | 'edit' | 'camera' | 'print';
export const COMPOSER_ACTION_FAMILIES: ComposerActionFamily[] = ['visibility', 'materials', 'edit', 'camera', 'print'];

export interface ComposerActionDefinition {
    key: string;
    family: ComposerActionFamily;
    type: ComposerOptionActionsType | ComposerUnsupportedActionType;
    name: string;
    description: string;
    valueLabel: string;
    icon: ComposerActionIcon;
    disabled?: boolean;
    /**
     * UN TIPO DEL MOTORE CHE NEL MENU NON HA UNA VOCE SUA (utente, 2026-10-21): ci si arriva dalla voce di questo tipo,
     * e nella card una scelta passa dall'uno all'altro (Hide da Set Visibility, Design da Swap Material, il pivot da
     * Move to Camera). Il nome della card resta quello della voce.
     */
    viaMenu?: ComposerOptionActionsType;
}

export const getComposerActionCatalog = (): ComposerActionDefinition[] => [
    {
        key: 'show',
        family: 'visibility',
        type: ComposerOptionActionsType.SHOW_OBJECTS,
        name: T._('Set Visibility', 'Admin'),
        description: T._('Bring objects into view', 'Admin'),
        valueLabel: '',
        icon: 'visibility'
    },
    {
        key: 'hide',
        family: 'visibility',
        type: ComposerOptionActionsType.HIDE_OBJECTS,
        name: T._('Set Visibility', 'Admin'),
        description: T._('Take objects out of view', 'Admin'),
        valueLabel: '',
        icon: 'visibilityOff',
        viaMenu: ComposerOptionActionsType.SHOW_OBJECTS
    },
    {
        key: 'material',
        family: 'materials',
        type: ComposerOptionActionsType.SET_OBJECT_MATERIAL,
        name: T._('Swap Material', 'Admin'),
        description: T._('Swap the material of whole objects', 'Admin'),
        valueLabel: T._('Material', 'Admin'),
        icon: 'object'
    },
    {
        key: 'submaterial',
        family: 'materials',
        type: ComposerOptionActionsType.SET_SUB_OBJECT_MATERIAL,
        name: T._('Change the material of a part', 'Admin'),
        description: T._('Swap it on one sub-object, not the whole thing', 'Admin'),
        valueLabel: T._('Material', 'Admin'),
        icon: 'part',
        // NON SI OFFRE PIÙ (utente, 2026-10-21: non supportato). Resta qui perché le azioni già salvate si leggano.
        disabled: true
    },
    {
        key: 'matcolor',
        family: 'materials',
        type: ComposerOptionActionsType.SET_MATERIAL_COLOR,
        name: T._('Swap Color', 'Admin'),
        description: T._('Same material, different color', 'Admin'),
        valueLabel: T._('Color', 'Admin'),
        icon: 'colour'
    },
    {
        key: 'camera',
        family: 'camera',
        type: ComposerOptionActionsType.MOVE_CAMERA,
        name: T._('Move to Camera', 'Admin'),
        description: T._('Frame the product from a saved camera', 'Admin'),
        valueLabel: T._('Camera', 'Admin'),
        icon: 'camera'
    },
    {
        key: 'design',
        family: 'materials',
        type: ComposerOptionActionsType.SET_DESIGN_MATERIAL,
        name: T._('Swap Material', 'Admin'),
        description: T._('The material the Designer area prints on', 'Admin'),
        valueLabel: T._('Design material', 'Admin'),
        icon: 'design',
        viaMenu: ComposerOptionActionsType.SET_OBJECT_MATERIAL
    },
    {
        key: 'pivot',
        family: 'camera',
        type: ComposerOptionActionsType.SET_CAMERA_PIVOT,
        name: T._('Move to Camera', 'Admin'),
        description: T._('The point the camera turns around', 'Admin'),
        valueLabel: T._('Pivot', 'Admin'),
        icon: 'pivot',
        viaMenu: ComposerOptionActionsType.MOVE_CAMERA
    },
    {
        key: 'pivotreset',
        family: 'camera',
        type: ComposerOptionActionsType.RESET_CAMERA_PIVOT,
        name: T._('Move to Camera', 'Admin'),
        description: T._('Put the turning point back at the center', 'Admin'),
        valueLabel: '',
        icon: 'reset',
        viaMenu: ComposerOptionActionsType.MOVE_CAMERA
    },
    {
        key: 'matparam',
        family: 'edit',
        type: ComposerOptionActionsType.SET_MATERIAL_PARAMETER,
        name: T._('Edit Material', 'Admin'),
        description: T._('One parameter of a material — roughness, opacity, metalness', 'Admin'),
        valueLabel: T._('Parameter', 'Admin'),
        icon: 'setting'
    },
    {
        key: 'printing',
        family: 'print',
        type: ComposerOptionActionsType.SET_PRINTING_METHOD,
        name: T._('Change the printing method', 'Admin'),
        description: T._('How the decoration is produced', 'Admin'),
        valueLabel: T._('Method', 'Admin'),
        icon: 'print'
    },
    {
        key: 'trigshow',
        family: 'visibility',
        type: 'SHOW_HOTSPOT',
        name: T._('Show a hotspot', 'Admin'),
        description: T._('Make a hotspot appear on the product', 'Admin'),
        valueLabel: T._('Hotspot', 'Admin'),
        icon: 'hotspot',
        disabled: true
    },
    {
        key: 'trighide',
        family: 'visibility',
        type: 'HIDE_HOTSPOT',
        name: T._('Hide a hotspot', 'Admin'),
        description: T._('Take a hotspot off the product', 'Admin'),
        valueLabel: T._('Hotspot', 'Admin'),
        icon: 'hotspot',
        disabled: true
    }
];

export const getComposerActionDefinition = (type: ComposerOptionActionsType): ComposerActionDefinition =>
    getComposerActionCatalog().find((definition) => definition.type === type) ?? getComposerActionCatalog()[0];

/** La voce del menu da cui si arriva a un tipo: la sua, o quella del tipo con cui è accorpato. */
export const getComposerActionMenuType = (type: ComposerOptionActionsType): ComposerOptionActionsType => getComposerActionDefinition(type).viaMenu ?? type;
